<template>
  <div class="line-push-message-log">
    <BobProPage
      ref="bobProPageRef"
      :columns="tableColumns"
      :tableConfig="tableConfig"
      :pageConfig="pageConfig"
      @refresh="onRefresh"
    >
      <!-- 統計數據 -->
      <template #before-table>
        <StatisticOverview
          v-if="!hideStatistics"
          v-loading="loading.statistics"
          :items="statisticsItems"
          class="mb-[20px]"
        />
      </template>
    </BobProPage>

    <!-- 通知類型選擇彈窗 -->
    <LinePushEventSelect
      v-if="showEventSelectModal"
      :selectedData="selectedEvents"
      :data="eventData"
      :typeOptions="eventTypeOptions"
      :loading="false"
      @confirm="onEventSelect"
      @close="showEventSelectModal = false"
    />

    <!-- 通知詳情 Drawer -->
    <LinePushMessageLogDetailDrawer
      v-if="modal.detail"
      :log-id="selectedLogId"
      :event-label-map="eventLabelMap"
      :event-data="eventData"
      @close="onCloseModal('detail')"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, reactive } from 'vue'
import { useBobProPage } from '@/use/useBobProPage'
import { useTable } from '@/use/table'
import BobProPage from '@/components/BobProPage.vue'
import StatisticOverview from '@/components/Dashboard/StatisticOverview.vue'
import LinePushEventSelect from '@/components/Select/LinePushEventSelect.vue'
import LinePushMessageLogDetailDrawer from './components/LinePushMessageLogDetailDrawer.vue'
import {
  GetLinePushMessageLog,
  GetLinePushMessageLogCount,
  GetLinePushMessageLogStatistics,
  GetLinePushEventTypes,
} from '@/api/linePushMessageLog'
import {
  linePushMessageStatusConfig,
} from '@/config/linePushMessageLog'
import { get, values } from 'lodash'
import { t } from '@/plugins/i18n/i18n'
import MemberSearch from '@/components/Search/MemberSearch.vue'
import SearchTextInput from '@/components/Input/SearchTextInput.vue'
import BaseDataSelect from '@/components/Select/BaseDataSelect.vue'
import { useRouter } from 'vue-router/composables'

export default defineComponent({
  name: 'LinePushMessageLog',
  components: {
    BobProPage,
    StatisticOverview,
    LinePushEventSelect,
    LinePushMessageLogDetailDrawer,
  },
  props: {
    // 預設會員 ID（用於會員詳情頁面）
    memberId: {
      type: String,
      default: undefined,
    },
    // 隱藏頁面標題（用於 tab 內嵌場景）
    hideTitle: {
      type: Boolean,
      default: false,
    },
    // 隱藏統計資訊
    hideStatistics: {
      type: Boolean,
      default: false,
    },
    // 隱藏的 filters（陣列形式）
    // 可用值: 'member', 'title', 'events', 'status', 'createdAtRange'
    hideFilters: {
      type: Array,
      default: () => [],
    },
    // 隱藏的表格欄位（陣列形式）
    // 可用值: 'createdAt', 'Member.UserInfo.name', 'Member.UserInfo.phone', 'event', 'title', 'status', 'operate'
    hideTableColumns: {
      type: Array,
      default: () => [],
    },
  },
  setup (props) {
    const router = useRouter()
    const { bobProPageRef, onRefresh, modal, onCloseModal } = useBobProPage()
    const { shopId, dateFormat } = useTable()

    const showEventSelectModal = ref(false)
    const selectedEvents = ref([]) // 儲存二維陣列: [['key1', 'key2'], ['key3']]
    const statistics = ref(null)
    const eventLabelMap = ref({}) // 儲存 key -> label 的對照表
    const eventData = ref([]) // 轉換後的事件資料給子組件使用
    const eventTypeOptions = ref([]) // 事件類型選項給子組件使用
    const selectedLogId = ref(null)

    /**
     * 轉換 API 資料為組件使用格式（不展開 keys）
     */
    const transformEventData = (apiData) => {
      const events = []
      const typeMap = {}
      const labelMap = {}

      apiData.forEach((item) => {
        const { event, eventType } = item

        // 建立事件類型對照表
        if (!typeMap[eventType.key]) {
          typeMap[eventType.key] = {
            value: eventType.key,
            label: eventType.displayName,
          }
        }

        // 建立 key -> displayName 的對照表
        event.key.forEach(key => {
          labelMap[key] = event.displayName
        })

        // 使用 keys.join(',') 作為唯一標識符
        events.push({
          id: event.key.join(','), // 用 join 作為唯一標識
          keys: event.key, // 保留原始陣列
          label: event.displayName,
          type: eventType.key,
          typeLabel: eventType.displayName,
        })
      })

      return { events, typeOptions: Object.values(typeMap), labelMap }
    }

    // 載入事件類型資料
    const loadEventTypes = async () => {
      try {
        const [res, err] = await GetLinePushEventTypes({
          shopId: shopId.value,
        })
        if (err) throw err

        const { events, typeOptions, labelMap } = transformEventData(res)
        eventData.value = events
        eventTypeOptions.value = typeOptions
        eventLabelMap.value = labelMap
      } catch (error) {
        console.error('載入事件類型失敗:', error)
        eventData.value = []
        eventTypeOptions.value = []
        eventLabelMap.value = {}
      }
    }

    // 根據 key 獲取顯示名稱
    const getEventLabel = (eventKey) => {
      return eventLabelMap.value[eventKey] || eventKey
    }

    const loading = reactive({
      statistics: false,
    })

    // 統計數據項目
    const statisticsItems = computed(() => (
      [
        {
          title: t('linePushMessageLog.statistics.totalCount'),
          value: statistics.value?.totalCount || 0,
        },
        {
          title: t('linePushMessageLog.statistics.successCount'),
          value: statistics.value?.successCount || 0,
        },
        {
          title: t('linePushMessageLog.statistics.failedCount'),
          value: statistics.value?.failedCount || 0,
        },
        {
          title: t('linePushMessageLog.statistics.pendingCount'),
          value: statistics.value?.pendingCount || 0,
        },
        {
          title: t('linePushMessageLog.statistics.lineMessageCount'),
          value: statistics.value?.lineMessageCount || 0,
          hint: t('linePushMessageLog.statistics.lineMessageCountHint'),
        },
      ]
    ))

    // 表格欄位定義
    const tableColumns = computed(() => {
      const allColumns = [
        {
          prop: 'createdAt',
          label: t('linePushMessageLog.table.column.createdAt'),
          component: 'div',
          columnAttrs: { minWidth: 180 },
          render: (row) => row.createdAt ? dateFormat(row.createdAt, 'YYYY/MM/DD HH:mm:ss') : '-',
        },
        {
          prop: 'Member.UserInfo.name',
          label: t('linePushMessageLog.table.column.memberName'),
          component: 'BaseElButton',
          columnAttrs: { minWidth: 150 },
          render: (row) => get(row, 'Member.UserInfo.name') || '-',
          props: () => ({
            type: 'text',
          }),
          on: (row) => ({
            click: () => {
              const memberId = row.MemberId
              if (!memberId) return
              router.push({
                name: 'MemberProfile',
                params: { id: memberId },
                query: { tab: 'info' },
              })
            },
          }),
        },
        {
          prop: 'Member.UserInfo.phone',
          label: t('linePushMessageLog.table.column.phone'),
          component: 'div',
          columnAttrs: { minWidth: 150 },
          render: (row) => get(row, 'Member.UserInfo.phone') || '-',
        },
        {
          prop: 'event',
          label: t('linePushMessageLog.table.column.event'),
          columnAttrs: { minWidth: 180 },
          component: 'div',
          render: (row) => getEventLabel(row.event) || row.event,
        },
        {
          prop: 'title',
          label: t('linePushMessageLog.table.column.title'),
          columnAttrs: { minWidth: 200 },
          component: 'div',
          render: (row) => row.title || '-',
        },
        {
          prop: 'status',
          label: t('linePushMessageLog.table.column.status'),
          columnAttrs: { minWidth: 120 },
          component: () => import('@/components/Tag/Tag.vue'),
          render: (row) => t(`linePushMessageLog.status.${row.status}`) || row.status,
          props: (row) => ({
            type: get(linePushMessageStatusConfig, `${row.status}.tagType`) || 'info',
          }),
        },
        {
          prop: 'operate',
          label: t('linePushMessageLog.table.column.operate'),
          fixed: 'right',
          width: 100,
          component: () => import('@/components/OperateColumn.vue'),
          props: (row) => ({
            items: [
              {
                key: 'detail',
                component: () => import('@/components/BaseElButton.vue'),
                render: () => t('linePushMessageLog.table.btn.detail'),
                props: {
                  type: 'text',
                  class: ['underline'],
                },
                on: {
                  click: () => {
                    selectedLogId.value = row.id
                    modal.detail = true
                  },
                },
              },
            ],
          }),
        },
      ]

      // 根據 hideTableColumns 過濾欄位
      return allColumns.filter(column => !props.hideTableColumns.includes(column.prop))
    })

    // 載入統計資料
    const loadStatistics = async (filters) => {
      loading.statistics = true
      try {
        const [res, err] = await GetLinePushMessageLogStatistics({
          shopId: shopId.value,
          memberId: props.memberId || filters.member?.id || undefined,
          title: filters.title || undefined,
          events: filters.events && filters.events.length ? filters.events.flat().join(',') : undefined,
          status: filters.status || undefined,
          createdAtStart: filters.createdAtStart || undefined,
          createdAtEnd: filters.createdAtEnd || undefined,
        })
        if (err) throw err
        statistics.value = res
      } catch (error) {
        window.$message.error(error)
      } finally {
        loading.statistics = false
      }
    }

    // 表格配置
    const tableConfig = computed(() => {
      // 先定義所有可能的 filters
      const allFilters = [
        // 會員 ID / 電話搜尋
        {
          key: 'member',
          component: MemberSearch,
          props: (filters) => ({
            placeholder: t('linePushMessageLog.filter.memberPlaceholder'),
            model: filters.member,
            class: 'col-span-2 !w-full member-search',
          }),
          on: (filters) => ({
            'update:model': (member) => {
              filters.member = member || undefined
            },
          }),
        },
        // 通知標題搜尋
        {
          key: 'title',
          component: SearchTextInput,
          props: (filters) => ({
            placeholder: t('linePushMessageLog.filter.titlePlaceholder'),
            value: filters.title,
            class: 'col-span-2 !w-full',
          }),
          on: (filters) => ({
            'update:value': (value) => {
              filters.title = value
            },
          }),
        },
        // 通知類型（彈窗選擇）
        {
          key: 'events',
          component: BaseDataSelect,
          props: (filters) => ({
            placeholder: t('linePushMessageLog.filter.eventTypePlaceholder'),
            value: filters.events,
            filterable: false,
            clearable: true,
            disabled: false,
            multiple: true,
            dataList: selectedEvents.value.map((keysArray) => {
              const firstKey = keysArray[0]
              const label = getEventLabel(firstKey)
              return {
                label,
                value: keysArray,
              }
            }),
            labelKey: 'label',
            objKey: 'value',
            popperClass: 'select-popper',
            collapseTags: true,
          }),
          on: (filters) => ({
            'update:value': (value) => {
              selectedEvents.value = value || []
              filters.events = value || []
            },
            visibleChange: (visible) => {
              // 只在打開下拉選單時觸發彈窗
              if (visible) {
                showEventSelectModal.value = true
              }
            },
            clear: () => {
              selectedEvents.value = []
              filters.events = []
              onRefresh()
            },
          }),
        },
        // 發送狀態
        {
          key: 'status',
          component: BaseDataSelect,
          props: (filters) => ({
            placeholder: t('linePushMessageLog.filter.statusPlaceholder'),
            value: filters.status,
            filterable: false,
            labelKey: 'label',
            objKey: 'value',
            dataList: values(linePushMessageStatusConfig).map((item) => ({
              ...item,
              label: t(`linePushMessageLog.status.${item.value}`),
            })),
          }),
          on: (filters) => ({
            'update:value': (value) => {
              filters.status = value
            },
          }),
        },
        // 發送日期範圍
        {
          key: 'createdAtRange',
          component: 'el-date-picker',
          props: (filters) => ({
            type: 'datetimerange',
            rangeSeparator: t('linePushMessageLog.filter.dateRangeSeparator'),
            startPlaceholder: t('linePushMessageLog.filter.dateRangeStart'),
            endPlaceholder: t('linePushMessageLog.filter.dateRangeEnd'),
            format: 'yyyy-MM-dd HH:mm',
            defaultTime: ['00:00:00', '23:59:59'],
            value: filters.createdAtStart && filters.createdAtEnd
              ? [filters.createdAtStart, filters.createdAtEnd]
              : null,
            class: 'col-span-2 !w-full',
          }),
          on: (filters) => ({
            input: (value) => {
              if (value && value.length === 2) {
                filters.createdAtStart = value[0]
                filters.createdAtEnd = value[1]
              } else {
                filters.createdAtStart = undefined
                filters.createdAtEnd = undefined
              }
            },
          }),
        },
      ]

      // 根據 props 條件過濾 filters
      const displayFilters = allFilters.filter(filter => {
        // 如果該 filter 在 hideFilters 陣列中，則排除
        return !props.hideFilters.includes(filter.key)
      })

      return {
        pagination: {
          enabled: true,
          pageLimit: 10,
          pageSizes: [10, 20, 50],
        },
        apis: {
          get: GetLinePushMessageLog,
          count: GetLinePushMessageLogCount,
          getPayload: (filters) => {
            // 同時載入統計資料
            if (!props.hideStatistics) {
              loadStatistics(filters)
            }
            return {
              memberId: props.memberId || filters.member?.id || undefined,
              title: filters.title || undefined,
              events: filters.events && filters.events.length ? filters.events.flat().join(',') : undefined,
              status: filters.status || undefined,
              createdAtStart: filters.createdAtStart || undefined,
              createdAtEnd: filters.createdAtEnd || undefined,
            }
          },
        },
        filters: displayFilters,
        tableAttrs: {
          stripe: true,
        },
      }
    })

    // 頁面配置
    const pageConfig = computed(() => ({
      title: props.hideTitle ? '' : t('linePushMessageLog.page.title'),
      titleSize: 18,
      actions: [],
    }))

    // 通知類型選擇確認
    const onEventSelect = (selectedKeysArrays) => {
      // selectedKeysArrays: [['key1', 'key2'], ['key3']]
      selectedEvents.value = selectedKeysArrays

      // 更新 BobProPage 的 search filter
      if (bobProPageRef.value) {
        bobProPageRef.value.search.events = selectedKeysArrays
      }

      // 觸發表格重新整理
      onRefresh()
    }

    // 初始化：載入事件類型對照表
    loadEventTypes()

    return {
      bobProPageRef,
      tableColumns,
      tableConfig,
      pageConfig,
      onRefresh,
      statisticsItems,
      loading,
      showEventSelectModal,
      selectedEvents,
      onEventSelect,
      eventData,
      eventTypeOptions,
      modal,
      selectedLogId,
      eventLabelMap,
      onCloseModal,
    }
  },
})
</script>

<style scoped lang="postcss">
:deep(.member-search) .el-input {
  @apply !w-full
}
</style>
